import {
  ZfBox,
  ZfContainer,
  ZfDropdown,
  ZfDropdownItem,
  ZfIcon,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';

import PortfolioTableItem, { PortfolioTableItemDetails } from './PortfolioTableItem';
import useGetPortfoliosList from './hooks/useGetPortfoliosList';
import { useParams } from 'react-router';
import useGetFeatureFlags from '../../components/portfolio/hooks/useGetFeatureFlags';
import { useEffect, useMemo, useState } from 'react';
import { FeatureFlagName } from '../../services/portfolios/portfolioService';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedPortfolio, setSelectedPortfolio } from '../../reducers/portfolioReducer';
import PortfolioDetails from './PortfolioDetails';
import { RootState } from '../../store';
import { getACAURL } from '../../utils/urlUtils';
import { JBEnvironmentId } from '@bjb-gcrm/gcrm-frontend-components';
import {
  PORTFOLIO_FILTER_ALL,
  PortfolioFilterDropdown,
  type PortfolioFilterValue,
} from './PortfolioSections/PortfolioFilterDropdown';
import { PortfolioStatusState } from '../../types/portfolios/portfolioData';
import { useTranslation } from 'react-i18next';
import { RoundDropdown } from '../../components/common/RoundDropdown';
import { useQueryClient } from '@tanstack/react-query';
import { openModal } from '../../reducers/modalReducer';
import { useGetServiceAgreementList } from '../../hooks/queries/privateBanking';

const PortfolioSummary = () => {
  const { t } = useTranslation();
  const { brUid } = useParams();
  const { isLoading: isRetrievingPortfolioList, data } = useGetPortfoliosList(brUid || '');
  const { isLoading: isRetrievingServiceAgreement, data: serviceAgreementListData } = useGetServiceAgreementList({
    businessRelationUid: brUid,
  });

  const env = useSelector((state: RootState) => state.appConfig.env);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const openCreatePortfolioDialog = () =>
    dispatch(
      openModal({
        modalType: 'AddPortfolioDialog',
        modalProps: {
          brUid,
          onProceed: () => queryClient.invalidateQueries({ queryKey: ['useGetPortfoliosList', brUid] }),
        },
      }),
    );

  const selectedPortfolio = useSelector(selectSelectedPortfolio);

  const { data: featureFlag, isLoading: featureFlagLoading } = useGetFeatureFlags();

  const [statusFilter, setStatusFilter] = useState<PortfolioFilterValue>(PortfolioStatusState.INITIAL);

  const portfolioData = useMemo(() => {
    const productLineNameByAgreementId = new Map(
      (serviceAgreementListData?.list ?? []).map((agreement) => [
        agreement.serviceAgreementId,
        { draftServiceModel: agreement.draftServiceModel, serviceModel: agreement.serviceModel },
      ]),
    );

    return (data?.list ?? []).map((portfolio) => {
      const productLine = portfolio.serviceAgreementId
        ? productLineNameByAgreementId.get(portfolio.serviceAgreementId)
        : undefined;

      return {
        ...portfolio,
        draftServiceModel: productLine?.draftServiceModel,
        serviceModel: productLine?.serviceModel,
      };
    });
  }, [data?.list, serviceAgreementListData?.list]);

  useEffect(() => {
    if (selectedPortfolio && portfolioData) {
      const updatedPortfolio = portfolioData?.find((item) => item.portfolioId === selectedPortfolio.portfolioId);
      if (updatedPortfolio) {
        dispatch(setSelectedPortfolio(updatedPortfolio as PortfolioTableItemDetails));
      }
    }
  }, [portfolioData, dispatch, selectedPortfolio]);

  const isLoading = useMemo(() => {
    return isRetrievingPortfolioList || isRetrievingServiceAgreement;
  }, [isRetrievingPortfolioList, isRetrievingServiceAgreement]);

  const filteredPortfolios = useMemo(() => {
    const list = portfolioData ?? [];

    if (statusFilter === PORTFOLIO_FILTER_ALL) {
      return list;
    }

    return list.filter((item) => item.portfolioStatus === statusFilter);
  }, [portfolioData, statusFilter]);

  const showPage = useMemo(() => {
    if (!featureFlagLoading) {
      return featureFlag?.find((item) => item.name === FeatureFlagName.PORTFOLIO_CREATE_API_ENABLED)?.enabled ?? false;
    }

    return false;
  }, [featureFlag, featureFlagLoading]);

  if (featureFlagLoading) {
    return (
      <ZfBox style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', paddingTop: 100 }}>
        <ZfLoadingSpinner size="extra-large" />
      </ZfBox>
    );
  }

  if (!showPage) {
    return null;
  }
  return (
    <ZfContainer
      style={{
        margin: 'var(--gap-horizontal-xl, 24px)',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-color-default-normal)',
        display: 'flex',
        height: 'calc(100vh - 48px)',
      }}
      disableGutters
    >
      <ZfStack
        spacing="medium"
        style={{
          width: selectedPortfolio ? '67%' : '100%',
          height: '100%',
          boxSizing: 'border-box',
          padding: 'var(--padding-top-xl, 24px)',
          transition: '0.1s ease',
          overflowY: 'auto',
        }}
      >
        <ZfStack direction="row" justify="space-between">
          <ZfTypography variant="title-large">{t('pb.portfoliosummary.title')}</ZfTypography>
        </ZfStack>
        <ZfStack direction="row" justify="flex-end" alignment="center" spacing="small">
          <ZfTypography>{t('pb.portfoliosummary.statusfilter.label')}</ZfTypography>
          <RoundDropdown
            data-testid="portfolio-status-filter"
            options={PortfolioFilterDropdown}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All"
          />
          <ZfDropdown
            data-testid="portfolio-more-actions"
            text={t('pb.portfoliosummary.moreactions')}
            hierarchy="secondary"
          >
            <ZfDropdownItem label={t('pb.portfoliosummary.preferredportfolioandcashaccounts')} state="disabled" />
            <ZfDropdownItem
              label={t('pb.portfoliosummary.acaclosurerequest')}
              onClick={() => window.open(getACAURL(env as JBEnvironmentId), '_blank', 'noopener,noreferrer')}
            >
              <ZfIcon name="open_in_new" slot="trailing" size="large" />
            </ZfDropdownItem>
          </ZfDropdown>
          <ZfTextButton hierarchy="primary" onClick={openCreatePortfolioDialog}>
            {t('pb.portfoliosummary.createportfolio')}
          </ZfTextButton>
        </ZfStack>
        <ZfStack spacing="medium">
          {filteredPortfolios.map((item, index) => (
            <PortfolioTableItem
              key={`portfolio_table_${index}`}
              title={item.portfolioNumber || ''}
              portfolio={item as PortfolioTableItemDetails}
            />
          ))}
          {!isLoading && filteredPortfolios.length === 0 && (
            <ZfTypography>{t('pb.portfoliosummary.empty')}</ZfTypography>
          )}
        </ZfStack>{' '}
      </ZfStack>
      {selectedPortfolio && (
        <ZfBox
          style={{
            width: '33%',
            height: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            borderLeft: '1px solid #e0e0e0',
            boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ZfBox style={{ flex: 1, overflowY: 'auto' }}>
            {selectedPortfolio && <PortfolioDetails portfolio={selectedPortfolio} />}
          </ZfBox>
        </ZfBox>
      )}
    </ZfContainer>
  );
};

export default PortfolioSummary;
